import React, { useState, useEffect } from 'react';
import { ordersService } from '../services/ordersService';
import { toast } from 'react-toastify';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Eye, 
  Search,
  Filter,
  Download,
  RefreshCw
} from 'lucide-react';
import '../styles/Orders.css';

const Orders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create', 'edit', 'view'
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    shipping_address: '',
    total_amount: '',
    order_data: '',
    order_status: 'pending'
  });

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response = await ordersService.getOrders();
      if (response.success) {
        setOrders(response.data || []);
      } else {
        toast.error('Failed to fetch orders');
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error('Error fetching orders: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOrder = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      shipping_address: '',
      total_amount: '',
      order_data: '',
      order_status: 'pending'
    });
    setModalMode('create');
    setShowModal(true);
  };

  const handleEditOrder = (order) => {
    setFormData({
      id: order.id,
      name: order.name,
      email: order.email,
      phone: order.phone,
      shipping_address: order.shipping_address,
      total_amount: order.total_amount,
      order_data: order.order_data,
      order_status: order.order_status
    });
    setSelectedOrder(order);
    setModalMode('edit');
    setShowModal(true);
  };

  const handleViewOrder = (order) => {
    setSelectedOrder(order);
    setModalMode('view');
    setShowModal(true);
  };

  const handleDeleteOrder = async (id) => {
    if (window.confirm('Are you sure you want to delete this order?')) {
      try {
        const response = await ordersService.deleteOrder(id);
        if (response.success) {
          toast.success('Order deleted successfully');
          fetchOrders();
        } else {
          toast.error('Failed to delete order');
        }
      } catch (error) {
        toast.error('Error deleting order');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let response;
      if (modalMode === 'create') {
        response = await ordersService.createOrder(formData);
      } else {
        response = await ordersService.updateOrder(formData);
      }

      if (response.success) {
        toast.success(`Order ${modalMode === 'create' ? 'created' : 'updated'} successfully`);
        setShowModal(false);
        fetchOrders();
      } else {
        toast.error(response.message || 'Operation failed');
      }
    } catch (error) {
      toast.error('Error saving order');
    }
  };

  const filteredOrders = orders.filter(order => {
    const matchesSearch = order.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         order.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         order.phone.includes(searchTerm);
    const matchesStatus = statusFilter === 'all' || order.order_status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'orange';
      case 'processing': return 'blue';
      case 'completed': return 'green';
      case 'cancelled': return 'red';
      default: return 'gray';
    }
  };

  return (
    <div className="orders-page">
      <div className="page-header">
        <div className="header-left">
          <h1>Orders Management</h1>
          <p>Manage customer orders and track order status</p>
        </div>
        <div className="header-actions">
          <button className="btn btn-outline" onClick={fetchOrders}>
            <RefreshCw size={16} />
            Refresh
          </button>
          <button className="btn btn-outline">
            <Download size={16} />
            Export
          </button>
          <button className="btn btn-primary" onClick={handleCreateOrder}>
            <Plus size={16} />
            New Order
          </button>
        </div>
      </div>

      <div className="page-controls">
        <div className="search-box">
          <Search size={20} className="search-icon" />
          <input
            type="text"
            placeholder="Search orders..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="filter-box">
          <Filter size={20} className="filter-icon" />
          <select 
            value={statusFilter} 
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="processing">Processing</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="loading">Loading orders...</div>
      ) : (
        <div className="orders-table">
            <table>
              <thead>
                <tr>
                  <th>Order ID</th>
                  <th>Customer</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredOrders.map(order => (
                  <tr key={order.id}>
                    <td>#{order.id}</td>
                    <td>{order.name}</td>
                    <td>{order.email}</td>
                    <td>{order.phone}</td>
                    <td>${parseFloat(order.total_amount || 0).toFixed(2)}</td>
                    <td>
                      <span className={`status ${getStatusColor(order.order_status)}`}>
                        {order.order_status}
                      </span>
                    </td>
                    <td>{new Date(order.created_at).toLocaleDateString()}</td>
                    <td>
                      <div className="action-buttons">
                        <button 
                          className="btn-icon" 
                          onClick={() => handleViewOrder(order)}
                          title="View Order"
                        >
                          <Eye size={16} />
                        </button>
                        <button 
                          className="btn-icon" 
                          onClick={() => handleEditOrder(order)}
                          title="Edit Order"
                        >
                          <Edit size={16} />
                        </button>
                        <button 
                          className="btn-icon danger" 
                          onClick={() => handleDeleteOrder(order.id)}
                          title="Delete Order"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredOrders.length === 0 && (
              <div className="no-data">No orders found</div>
            )}
          </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>
                {modalMode === 'create' && 'Create New Order'}
                {modalMode === 'edit' && 'Edit Order'}
                {modalMode === 'view' && 'Order Details'}
              </h2>
              <button 
                className="close-btn"
                onClick={() => setShowModal(false)}
              >
                ×
              </button>
            </div>

            {modalMode === 'view' ? (
              <div className="modal-body">
                <div className="order-details">
                  <div className="detail-row">
                    <label>Order ID:</label>
                    <span>#{selectedOrder?.id}</span>
                  </div>
                  <div className="detail-row">
                    <label>Customer Name:</label>
                    <span>{selectedOrder?.name}</span>
                  </div>
                  <div className="detail-row">
                    <label>Email:</label>
                    <span>{selectedOrder?.email}</span>
                  </div>
                  <div className="detail-row">
                    <label>Phone:</label>
                    <span>{selectedOrder?.phone}</span>
                  </div>
                  <div className="detail-row">
                    <label>Shipping Address:</label>
                    <span>{selectedOrder?.shipping_address}</span>
                  </div>
                  <div className="detail-row">
                    <label>Total Amount:</label>
                    <span>${parseFloat(selectedOrder?.total_amount || 0).toFixed(2)}</span>
                  </div>
                  <div className="detail-row">
                    <label>Order Data:</label>
                    <span>{selectedOrder?.order_data}</span>
                  </div>
                  <div className="detail-row">
                    <label>Status:</label>
                    <span className={`status ${getStatusColor(selectedOrder?.order_status)}`}>
                      {selectedOrder?.order_status}
                    </span>
                  </div>
                  <div className="detail-row">
                    <label>Created:</label>
                    <span>{new Date(selectedOrder?.created_at).toLocaleString()}</span>
                  </div>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="modal-body">
                <div className="form-grid">
                  <div className="form-group">
                    <label>Customer Name *</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Email *</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Phone *</label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Total Amount *</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.total_amount}
                      onChange={(e) => setFormData({...formData, total_amount: e.target.value})}
                      required
                    />
                  </div>
                  <div className="form-group full-width">
                    <label>Shipping Address</label>
                    <textarea
                      value={formData.shipping_address}
                      onChange={(e) => setFormData({...formData, shipping_address: e.target.value})}
                      rows="3"
                    />
                  </div>
                  <div className="form-group full-width">
                    <label>Order Data</label>
                    <textarea
                      value={formData.order_data}
                      onChange={(e) => setFormData({...formData, order_data: e.target.value})}
                      rows="3"
                    />
                  </div>
                  <div className="form-group">
                    <label>Status</label>
                    <select
                      value={formData.order_status}
                      onChange={(e) => setFormData({...formData, order_status: e.target.value})}
                    >
                      <option value="pending">Pending</option>
                      <option value="processing">Processing</option>
                      <option value="completed">Completed</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>
                </div>

                <div className="modal-footer">
                  <button type="button" className="btn btn-outline" onClick={() => setShowModal(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    {modalMode === 'create' ? 'Create Order' : 'Update Order'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Orders;